import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react';
import { Loader2 } from 'lucide-react';
import { cn } from './cn';
import {
  BUTTON_BASE,
  BUTTON_SIZE,
  BUTTON_VARIANT,
  type ButtonSize,
  type ButtonVariant,
} from './buttonStyles';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  loading?: boolean;
  leftIcon?: ReactNode;
  rightIcon?: ReactNode;
  iconOnly?: boolean;
  /** Substitui completamente as classes de cor do `variant` (não faz merge)
   *  — usado quando um contexto pontual precisa de uma cor de destaque
   *  diferente da paleta padrão (ex: CTA do gerador de creator, na cor do
   *  creator ativo). `undefined` mantém o comportamento normal do `variant`. */
  accentClassName?: string;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    variant = 'primary',
    size = 'md',
    loading,
    leftIcon,
    rightIcon,
    iconOnly,
    accentClassName,
    children,
    className,
    disabled,
    type,
    ...rest
  },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type ?? 'button'}
      disabled={disabled || loading}
      className={cn(
        BUTTON_BASE,
        'disabled:opacity-50 disabled:cursor-not-allowed',
        accentClassName ?? BUTTON_VARIANT[variant],
        BUTTON_SIZE[size],
        iconOnly && 'px-0 aspect-square',
        className,
      )}
      {...rest}
    >
      {loading ? (
        <Loader2 className="size-4 animate-spin" />
      ) : (
        leftIcon && <span className="shrink-0">{leftIcon}</span>
      )}
      {children && (
        <span className={cn(iconOnly && 'sr-only')}>{children}</span>
      )}
      {rightIcon && <span className="shrink-0">{rightIcon}</span>}
    </button>
  );
});
